/**
 * Floating feedback widget, shared by every site in this workspace.
 *
 * Usage (one call, no markup changes):
 *
 *   import { mountFeedback } from './feedback.js';
 *   const feedback = mountFeedback({ site: 'assetkit', email: 'hi@assetkit.top' });
 *   // ...and in the site's applyLanguage():
 *   feedback.setLang(getLang());
 *
 * The widget appends a launcher button plus a modal form to <body>, injects its
 * own styles, and POSTs JSON to `/api/feedback` (server side: api-feedback.js,
 * deployed as a Cloudflare Pages Function). Every submission carries the page
 * URL, user agent, viewport and language, so a report can be reproduced without
 * asking the user for them.
 *
 * If the endpoint is missing or fails, the panel keeps the typed message and
 * offers a prefilled mailto link, so feedback still reaches a human.
 *
 * Styling reads the Material tokens each site already defines
 * (`--md-sys-color-*`), so the widget picks up the host page's accent colour
 * without per-site CSS. The <style> block is injected once per page.
 */

const STRINGS = {
  zh: {
    launch: '反馈',
    title: '反馈与建议',
    intro: '遇到问题或想提建议？写在这里发给我，我会看到。',
    typeLegend: '类型',
    typeBug: '问题',
    typeIdea: '建议',
    typeOther: '其他',
    messageLabel: '具体内容',
    messagePlaceholder: '例如：导出位图字体时，中文字符少了一个「魔」字。',
    emailLabel: '邮箱（选填，方便我回复你）',
    emailPlaceholder: 'you@example.com',
    submit: '发送',
    sending: '发送中…',
    cancel: '取消',
    close: '关闭',
    successTitle: '已收到，谢谢！',
    successBody: '我会尽快查看。留了邮箱的话，我会回复你。',
    done: '关闭',
    errEmpty: '请先写点内容再发送。',
    errEmail: '邮箱格式看起来不对。',
    errTurnstile: '请先完成人机验证。',
    errNetwork: '发送失败，请检查网络后重试。',
    errServer: '发送失败，服务器暂时不可用。',
    errRate: '提交太频繁了，请稍后再试。',
    mailHint: '也可以直接把内容发到',
    mailAction: '改用邮件发送',
  },
  en: {
    launch: 'Feedback',
    title: 'Feedback & suggestions',
    intro: 'Found a bug or have an idea? Write it here and it reaches me directly.',
    typeLegend: 'Type',
    typeBug: 'Bug',
    typeIdea: 'Idea',
    typeOther: 'Other',
    messageLabel: 'Details',
    messagePlaceholder: 'e.g. the bitmap font export dropped the character "é".',
    emailLabel: 'Email (optional, so I can reply)',
    emailPlaceholder: 'you@example.com',
    submit: 'Send',
    sending: 'Sending…',
    cancel: 'Cancel',
    close: 'Close',
    successTitle: 'Received, thank you!',
    successBody: 'I will look into it soon. If you left an email, I will reply.',
    done: 'Close',
    errEmpty: 'Please write something first.',
    errEmail: 'That email address does not look right.',
    errTurnstile: 'Please complete the human check first.',
    errNetwork: 'Sending failed. Check your connection and try again.',
    errServer: 'Sending failed — the server is unavailable right now.',
    errRate: 'Too many submissions. Please try again later.',
    mailHint: 'You can also email',
    mailAction: 'Send as email instead',
  },
};

const TYPES = ['bug', 'idea', 'other'];

const LAUNCH_ICON =
  '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">' +
  '<path fill="currentColor" d="M12 3c5.52 0 10 3.58 10 8s-4.48 8-10 8c-1.06 0-2.08-.13-3.03-.37l-3.6 1.8a.6.6 0 0 1-.85-.66l.53-2.9C3.19 15.6 2 13.44 2 11c0-4.42 4.48-8 10-8Z"/>' +
  '</svg>';

const STYLE_ID = 'feedback-widget-style';

const CSS = `
/* The hidden attribute must win over the display rules below, otherwise the
   form and the success panel would both stay rendered. */
.fb-root [hidden] { display: none !important; }
.fb-root {
  --fb-primary: var(--md-sys-color-primary, #4355b9);
  --fb-on-primary: var(--md-sys-color-on-primary, #ffffff);
  --fb-surface: var(--md-sys-color-surface-container-lowest, var(--md-sys-color-surface, #ffffff));
  --fb-on-surface: var(--md-sys-color-on-surface, #1a1b20);
  --fb-muted: var(--md-sys-color-on-surface-variant, #46464f);
  --fb-outline: var(--md-sys-color-outline-variant, #c6c5d0);
  --fb-error: var(--md-sys-color-error, #ba1a1a);
  --fb-error-bg: var(--md-sys-color-error-container, #ffdad6);
  --fb-ok: #1b6b3a;
  font-family: inherit;
}
.fb-launch {
  position: fixed;
  right: 18px;
  bottom: 18px;
  z-index: 50;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 999px;
  background: var(--fb-primary);
  color: var(--fb-on-primary);
  font: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.22);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.fb-launch:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(0, 0, 0, 0.28); }
.fb-launch:focus-visible { outline: 3px solid var(--fb-primary); outline-offset: 3px; }
.fb-launch__icon { display: inline-flex; }
@media (max-width: 520px) {
  .fb-launch { right: 12px; bottom: 12px; padding: 11px; }
  .fb-launch__label { display: none; }
}
.fb-dialog {
  width: min(440px, calc(100vw - 24px));
  max-height: min(88vh, 720px);
  padding: 0;
  border: 1px solid var(--fb-outline);
  border-radius: 16px;
  background: var(--fb-surface);
  color: var(--fb-on-surface);
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.3);
  overflow: auto;
}
.fb-dialog::backdrop { background: rgba(0, 0, 0, 0.45); }
.fb-form { margin: 0; padding: 18px 18px 14px; display: grid; gap: 12px; }
.fb-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.fb-title { margin: 0; font-size: 1.05rem; font-weight: 700; }
.fb-intro { margin: 0; font-size: 0.85rem; line-height: 1.5; color: var(--fb-muted); }
.fb-iconbtn {
  border: none; background: none; color: var(--fb-muted); font-size: 1.4rem; line-height: 1;
  padding: 2px 6px; border-radius: 8px; cursor: pointer;
}
.fb-iconbtn:hover { background: rgba(127, 127, 127, 0.14); }
.fb-field { display: grid; gap: 6px; margin: 0; padding: 0; border: none; }
.fb-label, .fb-legend { font-size: 0.78rem; font-weight: 600; color: var(--fb-muted); }
.fb-types { display: flex; flex-wrap: wrap; gap: 8px; }
.fb-type { position: relative; display: inline-flex; }
.fb-type input {
  position: absolute; width: 1px; height: 1px; margin: 0; opacity: 0; pointer-events: none;
}
.fb-type span {
  display: inline-block; padding: 6px 14px; border: 1px solid var(--fb-outline);
  border-radius: 999px; font-size: 0.85rem; cursor: pointer; user-select: none;
}
.fb-type input:checked + span {
  background: var(--fb-primary); border-color: var(--fb-primary); color: var(--fb-on-primary);
}
.fb-type input:focus-visible + span { outline: 2px solid var(--fb-primary); outline-offset: 2px; }
.fb-textarea, .fb-input {
  width: 100%; box-sizing: border-box; font: inherit; font-size: 0.9rem;
  padding: 10px 12px; border: 1px solid var(--fb-outline); border-radius: 10px;
  background: var(--fb-surface); color: var(--fb-on-surface); resize: vertical;
}
.fb-textarea { min-height: 108px; }
.fb-textarea:focus, .fb-input:focus { outline: 2px solid var(--fb-primary); outline-offset: -1px; border-color: var(--fb-primary); }
.fb-hp { position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden; }
.fb-turnstile:empty { display: none; }
.fb-status { margin: 0; font-size: 0.85rem; line-height: 1.5; }
.fb-status[data-kind="error"] { color: var(--fb-error); }
.fb-status[data-kind="ok"] { color: var(--fb-ok); }
.fb-status[data-kind="busy"] { color: var(--fb-muted); }
.fb-mail { color: var(--fb-primary); }
.fb-actions { display: flex; justify-content: flex-end; gap: 8px; }
.fb-btn {
  font: inherit; font-size: 0.9rem; font-weight: 600; padding: 9px 18px;
  border-radius: 999px; cursor: pointer; border: 1px solid transparent;
}
.fb-btn--primary { background: var(--fb-primary); color: var(--fb-on-primary); }
.fb-btn--primary[disabled] { opacity: 0.6; cursor: default; }
.fb-btn--ghost { background: none; border-color: var(--fb-outline); color: var(--fb-on-surface); }
.fb-alt { margin: 0; font-size: 0.78rem; color: var(--fb-muted); line-height: 1.5; }
.fb-alt[data-prominent="true"] {
  padding: 8px 10px; border: 1px dashed var(--fb-outline); border-radius: 10px; font-weight: 600;
}
.fb-done { padding: 22px 18px; display: grid; gap: 12px; justify-items: start; }
.fb-done h2 { margin: 0; font-size: 1.05rem; }
.fb-done p { margin: 0; font-size: 0.88rem; line-height: 1.55; color: var(--fb-muted); }
@media (prefers-reduced-motion: reduce) {
  .fb-launch { transition: none; }
  .fb-launch:hover { transform: none; }
}
`;

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

function normalizeLang(value) {
  return String(value || '').toLowerCase().startsWith('zh') ? 'zh' : 'en';
}

// Site id used in the email subject. Kept to a safe charset so it can never
// break the subject header.
function normalizeSite(value) {
  const site = String(value || '').toLowerCase().replace(/[^a-z0-9-]/g, '');
  return site || 'unknown';
}

function siteHost() {
  return location.hostname.replace(/^www\./, '') || 'localhost';
}

export function mountFeedback(config = {}) {
  const existing = document.querySelector('[data-feedback-root]');
  if (existing && existing.__feedbackHandle) return existing.__feedbackHandle;
  if (config.disabled) return { setLang() {}, open() {}, close() {} };

  injectStyles();

  const site = normalizeSite(config.site || siteHost());
  const email = String(config.email || `hi@${siteHost()}`);
  const endpoint = String(config.endpoint || '/api/feedback');
  const turnstileSitekey = String(config.turnstileSitekey || '');

  let lang = normalizeLang(config.lang || document.documentElement.lang || navigator.language);
  let openedAt = 0;
  let turnstileToken = '';
  let turnstileLoaded = false;
  let turnstileWidgetId = null;
  let sending = false;

  const root = document.createElement('div');
  root.className = 'fb-root';
  root.setAttribute('data-feedback-root', site);
  root.innerHTML = `
    <button class="fb-launch" type="button" aria-haspopup="dialog">
      <span class="fb-launch__icon">${LAUNCH_ICON}</span>
      <span class="fb-launch__label"></span>
    </button>
    <dialog class="fb-dialog" aria-labelledby="fb-title">
      <form class="fb-form" novalidate>
        <div class="fb-head">
          <h2 class="fb-title" id="fb-title"></h2>
          <button class="fb-iconbtn" type="button" data-fb-close></button>
        </div>
        <p class="fb-intro"></p>
        <fieldset class="fb-field">
          <legend class="fb-legend"></legend>
          <div class="fb-types">
            ${TYPES.map(
              (type, i) =>
                `<label class="fb-type"><input type="radio" name="fb-type" value="${type}"${
                  i === 0 ? ' checked' : ''
                }><span data-fb-type="${type}"></span></label>`,
            ).join('')}
          </div>
        </fieldset>
        <label class="fb-field">
          <span class="fb-label" data-fb-message-label></span>
          <textarea class="fb-textarea" name="message" rows="5" maxlength="4000"></textarea>
        </label>
        <label class="fb-field">
          <span class="fb-label" data-fb-email-label></span>
          <input class="fb-input" name="email" type="email" maxlength="200" autocomplete="email" inputmode="email">
        </label>
        <div class="fb-hp" aria-hidden="true">
          <label>Website<input class="fb-hp-input" name="website" type="text" tabindex="-1" autocomplete="off"></label>
        </div>
        <div class="fb-turnstile"></div>
        <p class="fb-status" role="status" aria-live="polite"></p>
        <div class="fb-actions">
          <button class="fb-btn fb-btn--ghost" type="button" data-fb-close></button>
          <button class="fb-btn fb-btn--primary" type="submit"></button>
        </div>
        <p class="fb-alt"><span data-fb-mail-hint></span> <a class="fb-mail" href="#"></a></p>
      </form>
      <div class="fb-done" hidden>
        <h2 data-fb-done-title></h2>
        <p data-fb-done-body></p>
        <button class="fb-btn fb-btn--primary" type="button" data-fb-close></button>
      </div>
    </dialog>`;
  document.body.appendChild(root);

  const launchBtn = root.querySelector('.fb-launch');
  const launchLabel = root.querySelector('.fb-launch__label');
  const dialog = root.querySelector('.fb-dialog');
  const form = root.querySelector('.fb-form');
  const doneView = root.querySelector('.fb-done');
  const titleEl = root.querySelector('.fb-title');
  const introEl = root.querySelector('.fb-intro');
  const legendEl = root.querySelector('.fb-legend');
  const messageLabel = root.querySelector('[data-fb-message-label]');
  const emailLabel = root.querySelector('[data-fb-email-label]');
  const messageEl = root.querySelector('.fb-textarea');
  const emailEl = root.querySelector('.fb-input');
  const honeypotEl = root.querySelector('.fb-hp-input');
  const statusEl = root.querySelector('.fb-status');
  const submitBtn = root.querySelector('.fb-btn--primary[type="submit"]');
  const cancelBtn = root.querySelector('.fb-actions .fb-btn--ghost');
  const closeBtn = root.querySelector('.fb-iconbtn');
  const doneBtn = root.querySelector('.fb-done .fb-btn');
  const altEl = root.querySelector('.fb-alt');
  const mailHintEl = root.querySelector('[data-fb-mail-hint]');
  const mailLink = root.querySelector('.fb-mail');
  const turnstileBox = root.querySelector('.fb-turnstile');

  function tr(key) {
    return (STRINGS[lang] || STRINGS.en)[key];
  }

  // Subject/body for the mailto fallback: the user keeps whatever they typed.
  function mailtoHref() {
    const type = form.querySelector('input[name="fb-type"]:checked')?.value || 'other';
    const label = tr(`type${type[0].toUpperCase()}${type.slice(1)}`);
    const subject = `[${site}] ${label}`;
    const body = [messageEl.value.trim(), '', location.href].join('\n');
    return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  function setStatus(kind, text) {
    statusEl.dataset.kind = kind || '';
    statusEl.textContent = text || '';
  }

  function applyStrings() {
    launchLabel.textContent = tr('launch');
    launchBtn.setAttribute('aria-label', tr('launch'));
    titleEl.textContent = tr('title');
    introEl.textContent = tr('intro');
    legendEl.textContent = tr('typeLegend');
    for (const span of root.querySelectorAll('[data-fb-type]')) {
      span.textContent = tr(`type${span.dataset.fbType[0].toUpperCase()}${span.dataset.fbType.slice(1)}`);
    }
    messageLabel.textContent = tr('messageLabel');
    messageEl.placeholder = tr('messagePlaceholder');
    emailLabel.textContent = tr('emailLabel');
    emailEl.placeholder = tr('emailPlaceholder');
    submitBtn.textContent = sending ? tr('sending') : tr('submit');
    cancelBtn.textContent = tr('cancel');
    closeBtn.textContent = '×';
    closeBtn.setAttribute('aria-label', tr('close'));
    doneBtn.textContent = tr('done');
    root.querySelector('[data-fb-done-title]').textContent = tr('successTitle');
    root.querySelector('[data-fb-done-body]').textContent = tr('successBody');
    mailHintEl.textContent = tr('mailHint');
    mailLink.textContent = email;
    mailLink.href = mailtoHref();
    if (statusEl.dataset.code) setStatus(statusEl.dataset.kind, tr(statusEl.dataset.code));
  }

  // Turnstile is optional: loaded lazily on first open so pages that never use
  // the panel never fetch a third-party script.
  function loadTurnstile() {
    if (!turnstileSitekey || turnstileLoaded) return;
    turnstileLoaded = true;
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (!window.turnstile) return;
      turnstileWidgetId = window.turnstile.render(turnstileBox, {
        sitekey: turnstileSitekey,
        callback: (token) => {
          turnstileToken = token;
        },
        'expired-callback': () => {
          turnstileToken = '';
        },
        'error-callback': () => {
          turnstileToken = '';
        },
      });
    };
    document.head.appendChild(script);
  }

  function open() {
    if (dialog.open) return;
    openedAt = Date.now();
    doneView.hidden = true;
    form.hidden = false;
    statusEl.dataset.code = '';
    altEl.dataset.prominent = 'false';
    setStatus('', '');
    applyStrings();
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    loadTurnstile();
    messageEl.focus();
  }

  function close() {
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');
  }

  launchBtn.addEventListener('click', open);
  for (const btn of root.querySelectorAll('[data-fb-close]')) {
    btn.addEventListener('click', close);
  }
  dialog.addEventListener('click', (e) => {
    // Native <dialog> only fires clicks on the backdrop with the dialog itself
    // as the target; treat that as a dismiss.
    if (e.target === dialog) close();
  });
  form.addEventListener('input', () => {
    if (statusEl.dataset.kind === 'error') {
      statusEl.dataset.code = '';
      setStatus('', '');
    }
    altEl.dataset.prominent = 'false';
    mailLink.href = mailtoHref();
  });
  messageEl.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      form.requestSubmit();
    }
  });

  async function send() {
    if (sending) return;
    const message = messageEl.value.trim();
    const emailValue = emailEl.value.trim();
    if (!message) {
      statusEl.dataset.code = 'errEmpty';
      setStatus('error', tr('errEmpty'));
      messageEl.focus();
      return;
    }
    if (emailValue && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailValue)) {
      statusEl.dataset.code = 'errEmail';
      setStatus('error', tr('errEmail'));
      emailEl.focus();
      return;
    }
    if (turnstileSitekey && !turnstileToken) {
      statusEl.dataset.code = 'errTurnstile';
      setStatus('error', tr('errTurnstile'));
      return;
    }

    sending = true;
    statusEl.dataset.code = '';
    setStatus('busy', tr('sending'));
    submitBtn.disabled = true;
    applyStrings();

    const payload = {
      site,
      type: form.querySelector('input[name="fb-type"]:checked')?.value || 'other',
      message,
      email: emailValue,
      page: location.href,
      title: document.title,
      lang,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      ua: navigator.userAgent,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      elapsedMs: openedAt ? Date.now() - openedAt : 0,
      website: honeypotEl.value,
      turnstileToken,
    };

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    let response = null;
    let failure = 'errNetwork';
    try {
      response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
    } catch {
      failure = 'errNetwork';
    } finally {
      clearTimeout(timer);
    }

    if (response) {
      if (response.ok) {
        sending = false;
        // Start clean on the next open: the message was delivered, and a
        // Turnstile token is single-use.
        form.reset();
        turnstileToken = '';
        if (window.turnstile && turnstileWidgetId !== null) {
          window.turnstile.reset(turnstileWidgetId);
        }
        form.hidden = true;
        doneView.hidden = false;
        doneBtn.focus();
        return;
      }
      if (response.status === 429) failure = 'errRate';
      else failure = 'errServer';
    }

    // The panel stays open with the typed text intact, and the mailto line
    // below it becomes the escape hatch (it carries the same message).
    sending = false;
    submitBtn.disabled = false;
    statusEl.dataset.code = failure;
    altEl.dataset.prominent = 'true';
    setStatus('error', `${tr(failure)} ${tr('mailAction')} ↓`);
    applyStrings();
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    send();
  });

  const handle = {
    setLang(next) {
      const normalized = normalizeLang(next);
      if (normalized === lang) return;
      lang = normalized;
      applyStrings();
    },
    open,
    close,
  };
  root.__feedbackHandle = handle;

  applyStrings();
  return handle;
}
